import { Input } from '@starsoft/common/components';
import { CurrencyInputProps } from './props';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import styles from './styles.module.scss';
import { Image } from '../Image';
import { WalletService } from '@starsoft/common/services';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * Component that renders a currency input field with formatting and validation
 * Handles currency masking, unmasking, and displays a coin icon
 *
 * @component
 * @template T - Generic type for form values structure
 * @param {Object} props - Component props
 * @param {Object} props.coin - Coin object containing currency details
 * @param {number} props.value - Current input value
 * @param {boolean} props.isError - Whether input has validation error
 * @param {Function} props.setValue - Callback to update input value
 * @param {boolean} props.isLoading - Whether input is in loading state
 * @param {number} props.defaultValue - Default value for uncontrolled input
 * @param {boolean} props.hideCurrency - Whether to hide currency icon
 * @param {boolean} props.isExternalTouched - Whether field was touched externally
 * @returns {JSX.Element} Currency input field with formatting and coin icon
 */
export function CurrencyInput<T extends FieldValues>({
  coin,
  value,
  isError,
  setValue,
  isLoading,
  defaultValue,
  hideCurrency,
  isExternalTouched,
  ...props
}: CurrencyInputProps<T>) {
  const [touched, setTouched] = useState<boolean>(false);
  const [displayedValue, setDisplayedValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);

  /**
   * Sets the touched state to true if not already touched
   */
  function handleSetTouched() {
    if (touched) {
      return;
    }
    setTouched(true);
  }

  /**
   * Formats a numeric value into a masked currency string
   * @param {string} value - Numeric value to mask
   * @returns {string} Masked currency string
   */
  const getMaskedValue = useCallback(
    (value: string): string => {
      return WalletService.maskCurrency({
        amount: getMoneyOrAmount(+value),
        coin,
        hideSymbol: true,
      });
    },
    [coin],
  );

  /**
   * Converts a masked currency string back to a number
   * @param {string} value - Masked currency string to unmask
   * @returns {number} Unmasked numeric value
   */
  const getUnmaskedValue = useCallback(
    (value: string): number => {
      return WalletService.unmaskCurrency({
        amount: value,
        coin,
      });
    },
    [coin],
  );

  /**
   * Handles input change events
   * Filters non-numeric characters and updates value
   * @param {ChangeEvent<HTMLInputElement>} e - Change event
   */
  function onChange(e: ChangeEvent<HTMLInputElement>) {
    handleSetTouched();

    const inputValue: string = e.target.value.replace(/[^0-9,.]/g, '') || '';
    const unmaskedValue: number = WalletService.unmaskCurrency({
      amount: inputValue,
      coin,
    });

    setValue(!inputValue ? 0 : unmaskedValue);
    setDisplayedValue(inputValue);
  }

  /**
   * Handles input blur events
   * Formats value and updates display
   */
  function onBlur() {
    handleSetTouched();
    setIsFocused(false);
    const formattedValue: string = getMaskedValue(String(value || 0));
    setDisplayedValue(!value ? '' : formattedValue);
  }

  /**
   * Handles input focus events
   */
  function onFocus() {
    setIsFocused(true);
  }

  /**
   * Resets input state when coin changes
   */
  function onCoinChange() {
    if (!coin) {
      return;
    }

    setValue(0);
    setTouched(false);
    setDisplayedValue('');
  }

  // eslint-disable-next-line
  useEffect(onCoinChange, [coin]);

  /**
   * Updates displayed value when focused
   */
  function handleFocus() {
    if (isFocused) {
      return;
    }

    const formattedValue: string = getMaskedValue(String(value || 0));
    setDisplayedValue(!value ? '' : formattedValue);
    setValue(getUnmaskedValue(formattedValue));
  }

  //eslint-disable-next-line
  useEffect(handleFocus, [value, getMaskedValue, isFocused, getUnmaskedValue]);

  return (
    <Input
      onChange={onChange}
      type="string"
      defaultValue={
        typeof defaultValue !== 'undefined' && coin
          ? getMaskedValue(String(defaultValue))
          : undefined
      }
      hasPaddingRight
      isLoading={!coin || isLoading}
      isError={isError && (touched || isExternalTouched)}
      onBlur={onBlur}
      onFocus={onFocus}
      value={displayedValue}
      customIcon={
        !!coin &&
        !hideCurrency && (
          <Image
            className={styles.coin}
            alt={`coin-icon-${coin?.name}`}
            width={20}
            height={20}
            src={`${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${coin?.image}`}
          />
        )
      }
      {...props}
      placeholder={getMaskedValue('0')}
    />
  );
}
